<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'

import { Carousel, getScaledPx } from '@collector/shared-ui'
import { Result, ResultId } from '@collector/sportsapi-client-legacy'
import {
  useEvent,
  useParticipantResultsMaps,
} from '@desktop/views/Relation/composables'
import { injectResultWithSliderConfig } from '@desktop/views/Relation/Sports/provide-inject'
import { useElementSize } from '@vueuse/core'

import ResultItem from './ResultItem.vue'

// composables
const { homeParticipantResultsMap, awayParticipantResultsMap } =
  useParticipantResultsMaps()
const { event } = useEvent()

// inject
const { eventStatusToResultIdMap, resultIdToAcronymMap, showCurrentResult } =
  injectResultWithSliderConfig()!

// state
const containerElement = useTemplateRef('containerElement')
const { width: containerWidth } = useElementSize(containerElement)
const shouldShowNavigation = ref(false)
const resultWidthsMap = ref(new Map<ResultId, number>())
const resultWidths = ref<number[]>([])

// computed
const currentResultId = computed(() =>
  eventStatusToResultIdMap.get(event.value.status_id),
)

const arrayFromAcronymMap = computed(() => {
  return Array.from(resultIdToAcronymMap, ([resultId, acronym]) => ({
    resultId,
    acronym,
  })).filter(({ resultId }) => hasResultIdValue(resultId))
})

const totalResultsWidth = computed(() => {
  return resultWidths.value.reduce((acc, width) => acc + width, 0)
})

function changeResultWidth(width: number, resultId: ResultId): void {
  const previousWidth = resultWidthsMap.value.get(resultId)
  if (previousWidth !== width) {
    resultWidthsMap.value.set(resultId, width)
    resultWidths.value = Array.from(resultWidthsMap.value.values())
  }
}

function decreaseResultWidths(newLength: number): void {
  if (resultWidths.value.length > newLength) {
    resultWidths.value = resultWidths.value.slice(0, newLength)
  }
}

// methods
function hasParticipantResultId(
  participantResultMap: Record<ResultId, Result>,
  resultId: ResultId,
): boolean {
  const result = participantResultMap[resultId]

  return result && result.value !== null
}

function hasResultIdValue(resultId: ResultId): boolean {
  return (
    hasParticipantResultId(homeParticipantResultsMap.value, resultId) &&
    hasParticipantResultId(awayParticipantResultsMap.value, resultId)
  )
}

function calculateOffset(): number {
  const offset = shouldShowNavigation.value ? 60 : 20

  return getScaledPx(offset)
}

function calculateAverageWidth(totalWidth: number, length: number): number {
  return totalWidth / length
}

// init
watch(
  [containerWidth, arrayFromAcronymMap, totalResultsWidth],
  ([_, newArrayFromAcronymMap, newTotalResultsWidth]) => {
    decreaseResultWidths(newArrayFromAcronymMap.length)

    const offset = calculateOffset()

    const averageOfTotalResultsWidth = calculateAverageWidth(
      newTotalResultsWidth,
      newArrayFromAcronymMap.length,
    )

    shouldShowNavigation.value =
      newTotalResultsWidth + averageOfTotalResultsWidth / 2 >
      containerWidth.value - offset
  },
  { immediate: true },
)
</script>

<template>
  <div class="flex min-h-4 w-full justify-center">
    <div
      ref="containerElement"
      class="flex w-[95%] justify-center text-xs"
    >
      <Carousel
        :initialIndex="arrayFromAcronymMap.length - 1"
        :items="arrayFromAcronymMap"
        :shouldShowNavigation
      >
        <template #item="{ item }">
          <ResultItem
            v-if="currentResultId !== item.resultId || showCurrentResult"
            :resultAcronym="item.acronym"
            :resultId="item.resultId"
            :currentResultId
            @widthCalculated="
              (width) => changeResultWidth(width, item.resultId)
            "
          />
        </template>
      </Carousel>
    </div>
  </div>
</template>
